import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, fieldErrors, unwrap, type Schemas } from '../../api/client';
import type { AssetDetail } from '../../api/types';
import { AssetLink } from '../../components/assets/AssetLink';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DateInput,
  ErrorBox,
  Field,
  FormGrid,
  Input,
  Loading,
  Notice,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../../components/ui';
import { computeBulkPrices, type BulkRow } from '../../lib/bulk';
import { centsToInput, formatDate, formatMoney, parseMoney, VAT_PERCENT } from '../../lib/format';

/**
 * Mehrere Geräte gemeinsam bearbeiten, z. B. nach einem Sammelkauf: gemeinsame Kauf-/Buchhaltungsdaten oben,
 * Gesamtpreis wird verteilt, jede Zeile bleibt einzeln änderbar. Ein Klick speichert alles (ganz oder gar nicht).
 */

type Common = {
  purchase_date: string;
  capitalization_date: string;
  commissioning_date: string;
  warranty_until: string;
  supplier: string;
  invoice_number: string;
  order_number: string;
};
const EMPTY_COMMON: Common = {
  purchase_date: '',
  capitalization_date: '',
  commissioning_date: '',
  warranty_until: '',
  supplier: '',
  invoice_number: '',
  order_number: '',
};

type RowState = BulkRow & { accounting: string };

const initialRow = (a: AssetDetail): RowState => ({
  netManual: a.cost_net_cents !== null ? centsToInput(a.cost_net_cents) : null,
  grossManual: a.cost_gross_cents !== null ? centsToInput(a.cost_gross_cents) : null,
  accounting: a.accounting_asset_number ?? '',
});

const money = (s: string): number | null => {
  const v = parseMoney(s);
  if (Number.isNaN(v)) throw new Error('Bitte Beträge im Format 1.234,56 eingeben.');
  return v;
};

export function BulkEditPage() {
  const [params] = useSearchParams();
  const refs = useMemo(() => (params.get('assets') ?? '').split(',').map((s) => s.trim()).filter(Boolean), [params]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const queries = useQueries({
    queries: refs.map((ref) => ({
      queryKey: ['asset', ref],
      queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref } } })),
    })),
  });
  const loading = queries.some((q) => q.isPending);
  const failed = queries.find((q) => q.error)?.error;
  const all = queries.flatMap((q) => (q.data ? [q.data] : []));
  const assets = all.filter((a) => a.status !== 'disposed');
  const disposed = all.filter((a) => a.status === 'disposed');

  const [common, setCommon] = useState<Common>(EMPTY_COMMON);
  const [total, setTotal] = useState('');
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loading && !loaded && assets.length) {
      setRows(Object.fromEntries(assets.map((a) => [a.id, initialRow(a)])));
      setLoaded(true);
    }
  }, [loading, loaded, assets]);

  const rowList = assets.map((a) => rows[a.id] ?? initialRow(a));
  const prices = computeBulkPrices(total, rowList);
  const setRow = (id: string, patch: Partial<RowState>) => setRows((r) => ({ ...r, [id]: { ...(r[id] as RowState), ...patch } }));

  const save = useMutation({
    mutationFn: () => {
      const items: Schemas['BulkUpdateAssets']['items'] = assets.map((a, i) => {
        const item: Schemas['BulkUpdateAssets']['items'][number] = { ref: a.id, version: a.version };
        for (const [k, v] of Object.entries(common) as Array<[keyof Common, string]>) {
          if (v.trim()) (item as Record<string, unknown>)[k] = v.trim();
        }
        const p = prices.rows[i]!;
        const net = money(p.net);
        const gross = money(p.gross);
        if (net !== a.cost_net_cents) item.cost_net_cents = net;
        if (gross !== a.cost_gross_cents) item.cost_gross_cents = gross;
        const acc = rowList[i]!.accounting.trim();
        if (acc !== (a.accounting_asset_number ?? '')) item.accounting_asset_number = acc;
        return item;
      });
      return unwrap(api.POST('/api/assets/bulk-update', { body: { items } }));
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
      toast(r.updated ? `${r.updated} Gerät${r.updated === 1 ? '' : 'e'} gespeichert` : 'Keine Änderungen');
      navigate('/assets');
    },
  });

  // Fehler je Zeile (items.N.feld) dem Gerät zuordnen
  const rowErrors = useMemo(() => {
    const out: Record<number, string> = {};
    for (const [path, msg] of Object.entries(fieldErrors(save.error))) {
      const m = /^items\.(\d+)\./.exec(path);
      if (m && !out[Number(m[1])]) out[Number(m[1])] = msg;
    }
    return out;
  }, [save.error]);

  const back = (
    <Link to="/assets" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
      <ArrowLeft className="size-4" /> Assets
    </Link>
  );

  if (!refs.length) {
    return (
      <>
        <PageHeader back={back} title="Gemeinsam bearbeiten" />
        <Notice>In der Assets-Übersicht Geräte anhaken und dann „Gemeinsam bearbeiten“ wählen.</Notice>
      </>
    );
  }
  if (loading) return <Loading />;
  if (failed) return <ErrorBox error={failed} />;

  const bindCommon = (k: keyof Common) => ({ value: common[k], onChange: (e: { target: { value: string } }) => setCommon((c) => ({ ...c, [k]: e.target.value })) });

  return (
    <>
      <PageHeader
        back={back}
        title={`${assets.length} Geräte gemeinsam bearbeiten`}
        subtitle="Z. B. nach einem Sammelkauf. Leere Felder bleiben unverändert."
      />
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Card>
          <CardHeader title="Für alle Geräte" description="Nur ausgefüllte Felder werden bei allen Geräten gesetzt – auch wenn dort schon etwas steht." />
          <CardBody>
            <FormGrid className="lg:grid-cols-4">
              <Field label="Kaufdatum">{(p) => <DateInput {...p} {...bindCommon('purchase_date')} />}</Field>
              <Field label="Aktivierungsdatum (AHK)">{(p) => <DateInput {...p} {...bindCommon('capitalization_date')} />}</Field>
              <Field label="Inbetriebnahme">{(p) => <DateInput {...p} {...bindCommon('commissioning_date')} />}</Field>
              <Field label="Garantie bis">{(p) => <DateInput {...p} {...bindCommon('warranty_until')} />}</Field>
              <Field label="Lieferant">{(p) => <Input {...p} {...bindCommon('supplier')} />}</Field>
              <Field label="Rechnungsnummer">{(p) => <Input {...p} {...bindCommon('invoice_number')} />}</Field>
              <Field label="Bestellnummer">{(p) => <Input {...p} {...bindCommon('order_number')} />}</Field>
              <Field
                label="Gesamtpreis netto (€)"
                hint={`Wird auf die Geräte ohne eigenen Preis verteilt; Brutto jeweils + ${VAT_PERCENT} % USt.`}
              >
                {(p) => <Input {...p} inputMode="decimal" placeholder="0,00" value={total} onChange={(e) => setTotal(e.target.value)} />}
              </Field>
            </FormGrid>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Einzelne Geräte"
            description="Preise und Anlagennummer je Gerät anpassbar. Ein eigener Preis bleibt fest; Feld leeren = wieder aus dem Gesamtpreis verteilen."
          />
          <Table>
            <thead>
              <tr>
                <Th>Nr.</Th>
                <Th>Bezeichnung</Th>
                <Th>Kaufdatum bisher</Th>
                <Th>Kosten netto (€)</Th>
                <Th>Kosten brutto (€)</Th>
                <Th>Anlagennummer</Th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a, i) => {
                const p = prices.rows[i]!;
                const r = rowList[i]!;
                return (
                  <Tr key={a.id} className={rowErrors[i] ? 'bg-red-50' : undefined}>
                    <Td className="whitespace-nowrap">
                      <AssetLink asset={a} />
                    </Td>
                    <Td>
                      <div className="font-medium text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500">{[a.asset_type.label, a.manufacturer, a.model].filter(Boolean).join(' · ')}</div>
                      {rowErrors[i] && <div className="text-xs text-red-700">{rowErrors[i]}</div>}
                    </Td>
                    <Td className="whitespace-nowrap text-slate-500">{formatDate(a.purchase_date)}</Td>
                    <Td className="min-w-32">
                      <Input
                        aria-label={`Kosten netto ${a.asset_number}`}
                        inputMode="decimal"
                        placeholder="0,00"
                        value={p.net}
                        className={p.netAuto ? 'bg-slate-50 text-slate-600' : undefined}
                        onChange={(e) => setRow(a.id, { netManual: e.target.value.trim() ? e.target.value : null })}
                      />
                      {p.netAuto && <div className="mt-0.5 text-xs text-slate-400">aus Gesamtpreis</div>}
                    </Td>
                    <Td className="min-w-32">
                      <Input
                        aria-label={`Kosten brutto ${a.asset_number}`}
                        inputMode="decimal"
                        placeholder="0,00"
                        value={p.gross}
                        className={p.grossAuto ? 'bg-slate-50 text-slate-600' : undefined}
                        onChange={(e) => setRow(a.id, { grossManual: e.target.value.trim() ? e.target.value : null })}
                      />
                      {p.grossAuto && <div className="mt-0.5 text-xs text-slate-400">+ {VAT_PERCENT} % USt.</div>}
                    </Td>
                    <Td className="min-w-36">
                      <Input aria-label={`Anlagennummer ${a.asset_number}`} value={r.accounting} onChange={(e) => setRow(a.id, { accounting: e.target.value })} />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <CardBody className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 text-sm">
            <span className="text-slate-600">
              Summe netto: <strong>{formatMoney(prices.sum)}</strong>
              {prices.total !== null && <> · Gesamtpreis: {formatMoney(prices.total)}</>}
            </span>
            {prices.mismatch && (
              <span className="text-amber-700">Die Einzelpreise ergeben nicht den Gesamtpreis – bitte prüfen (Speichern ist trotzdem möglich).</span>
            )}
          </CardBody>
        </Card>

        {disposed.length > 0 && (
          <Notice tone="warning">
            Verschrottet und nicht änderbar, wird übersprungen: {disposed.map((a) => a.asset_number).join(', ')}
          </Notice>
        )}
        <ErrorBox error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={() => navigate('/assets')}>Abbrechen</Button>
          <Button type="submit" variant="primary" icon={<Save className="size-4" />} loading={save.isPending} disabled={!assets.length}>
            Alle speichern
          </Button>
        </div>
      </form>
    </>
  );
}
